import { useState } from "react";

import { useNavigate } from "react-router-dom";

import dayjs from "dayjs";
import {
  ArrowLeft,
  ArrowRightLeft,
  Boxes,
  Calendar,
  Filter,
  Layers,
  MapPin,
  Package,
  Search,
} from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { usePalletStatistics, usePallets, useWarehouses } from "~/hooks/wms";
import type {
  Pallet,
  PalletFilters,
  PalletStatus,
  PalletType,
} from "~/types/wms";
import { PALLET_STATUS_LABELS, PALLET_TYPE_LABELS } from "~/types/wms";

const statusConfig: Record<
  PalletStatus,
  {
    variant: "default" | "secondary" | "destructive" | "outline";
    color: string;
  }
> = {
  created: { variant: "secondary", color: "text-gray-500" },
  ready_for_warehouse: { variant: "default", color: "text-blue-500" },
  stored: { variant: "default", color: "text-green-500" },
  partial: { variant: "default", color: "text-yellow-500" },
  picked_full: { variant: "outline", color: "text-purple-500" },
  empty: { variant: "secondary", color: "text-gray-400" },
};

export function PalletsPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<PalletStatus | "all">("all");
  const [typeFilter, setTypeFilter] = useState<PalletType | "all">("all");
  const [warehouseFilter, setWarehouseFilter] = useState<string>("all");

  const { data: warehouses = [] } = useWarehouses();

  const filters: PalletFilters = {
    ...(statusFilter !== "all" && { status: statusFilter }),
    ...(typeFilter !== "all" && { pallet_type: typeFilter }),
    ...(warehouseFilter !== "all" && { warehouse_id: warehouseFilter }),
    ...(searchQuery && { search: searchQuery }),
  };

  const { data: pallets = [], isLoading } = usePallets(filters);
  const { data: stats } = usePalletStatistics(
    warehouseFilter !== "all" ? warehouseFilter : undefined
  );

  const handleRowClick = (pallet: Pallet) => {
    navigate(`/wms/pallets/${pallet.id}`);
  };

  const handleRelocate = () => {
    navigate("/wms/pallet-relocation");
  };

  const getProductNames = (pallet: Pallet): string => {
    if (pallet.batches && pallet.batches.length > 0) {
      const names = pallet.batches
        .map((b) => b.product?.short_name)
        .filter(Boolean);
      if (names.length > 2) {
        return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
      }
      return names.join(", ");
    }
    return pallet.product?.short_name || "-";
  };

  const getPositionDisplay = (pallet: Pallet): string => {
    const pos = pallet.position as any;
    if (!pos) return "-";
    return pos.full_code || pos.code || "-";
  };

  return (
    <PageContainer>
      <PageHeader
        title="Pallets"
        description="Gestión y seguimiento de pallets en bodega"
        back={
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-9 text-muted-foreground"
            onClick={() => navigate("/wms")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
        }
        actions={
          <Button onClick={handleRelocate}>
            <ArrowRightLeft className="mr-2 h-4 w-4" />
            Reubicar Pallet
          </Button>
        }
      />

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <Package className="h-6 w-6 text-gray-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Almacenados</p>
                  <p className="text-2xl font-bold">{stats.stored}</p>
                </div>
                <MapPin className="h-6 w-6 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Parciales</p>
                  <p className="text-2xl font-bold">{stats.partial}</p>
                </div>
                <Layers className="h-6 w-6 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Vacíos</p>
                  <p className="text-2xl font-bold">{stats.empty}</p>
                </div>
                <Package className="h-6 w-6 text-gray-300" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Mixtos</p>
                  <p className="text-2xl font-bold">{stats.mixed}</p>
                </div>
                <Boxes className="h-6 w-6 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Cajas</p>
                  <p className="text-2xl font-bold">
                    {stats.totalBoxes.toLocaleString()}
                  </p>
                </div>
                <Boxes className="h-6 w-6 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Filter className="h-4 w-4" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-4">
            <div className="min-w-[200px] flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar por código o barcode..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <Select
              value={warehouseFilter}
              onValueChange={(v) => setWarehouseFilter(v)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Bodega" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las bodegas</SelectItem>
                {warehouses.map((w) => (
                  <SelectItem key={w.id} value={w.id}>
                    {w.code} - {w.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as PalletStatus | "all")}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                {Object.entries(PALLET_STATUS_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={typeFilter}
              onValueChange={(v) => setTypeFilter(v as PalletType | "all")}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                {Object.entries(PALLET_TYPE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Pallets Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Package className="h-4 w-4" />
            Pallets ({pallets.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : pallets.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <Package className="mx-auto h-12 w-12 opacity-30" />
              <p className="mt-2">No se encontraron pallets</p>
              {searchQuery && (
                <p className="text-sm">
                  Intenta con otros términos de búsqueda
                </p>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Producto(s)</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="text-right">Cajas</TableHead>
                  <TableHead>Ubicación</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Vencimiento</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pallets.map((pallet, index) => {
                  const config = statusConfig[pallet.status];
                  const expirationDate =
                    pallet.earliest_expiration || pallet.expiration_date;

                  return (
                    <TableRow
                      key={pallet.id}
                      style={{
                        animationDelay: `${Math.min(index * 40, 400)}ms`,
                      }}
                      className="cursor-pointer duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-muted/50"
                      onClick={() => handleRowClick(pallet)}
                    >
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-mono font-semibold">
                            {pallet.code}
                          </span>
                          {pallet.barcode && (
                            <span className="text-xs text-muted-foreground">
                              {pallet.barcode}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="max-w-[200px] truncate">
                          {getProductNames(pallet)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            pallet.pallet_type === "mixed"
                              ? "default"
                              : "outline"
                          }
                          className={
                            pallet.pallet_type === "mixed"
                              ? "bg-purple-100 text-purple-700 hover:bg-purple-200"
                              : ""
                          }
                        >
                          {PALLET_TYPE_LABELS[pallet.pallet_type]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-medium">
                          {pallet.current_box_count}
                        </span>
                        <span className="text-muted-foreground">
                          /{pallet.boxes_per_pallet}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3 w-3 text-muted-foreground" />
                          <span className="font-mono text-sm">
                            {getPositionDisplay(pallet)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={config.variant}>
                          {PALLET_STATUS_LABELS[pallet.status]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {expirationDate ? (
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3 text-muted-foreground" />
                            <span
                              className={
                                dayjs(expirationDate).isBefore(
                                  dayjs().add(30, "day")
                                )
                                  ? "font-medium text-red-600"
                                  : ""
                              }
                            >
                              {dayjs(expirationDate).format("DD/MM/YYYY")}
                            </span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
}

export default PalletsPage;
